import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  useClassInstructors,
  useInstructors,
  useAssignInstructor,
  useUnassignInstructor,
} from '@/data/instructors';
import { useClassSessions, useGenerateSessions, useCancelSession } from '@/data/sessions';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { formatTime12 } from '@/lib/time';
import { toast } from '@/lib/toast';
import type { Class } from '@pirouette/shared';

export const Route = createFileRoute('/admin/classes/$id')({
  component: ClassDetail,
});

function useClassById(id: string) {
  return useQuery({
    queryKey: ['classes', id],
    queryFn: async (): Promise<Class | null> => {
      const { data, error } = await supabase
        .from('classes')
        .select('*')
        .eq('id', id)
        .is('deleted_at', null)
        .maybeSingle();
      if (error) throw error;
      return data as Class | null;
    },
  });
}

function ClassDetail() {
  const { id } = Route.useParams();
  const classQuery = useClassById(id);
  const instructors = useInstructors();
  const assigned = useClassInstructors(id);
  const sessions = useClassSessions({ classId: id });
  const assign = useAssignInstructor();
  const unassign = useUnassignInstructor();
  const generateSessions = useGenerateSessions();
  const cancelSession = useCancelSession();

  if (classQuery.isLoading) return <p aria-live="polite">Loading…</p>;
  if (classQuery.isError || !classQuery.data) {
    return (
      <p role="alert" className="text-[var(--color-danger)]">
        Class not found.
      </p>
    );
  }

  const c = classQuery.data;
  const assignedIds = new Set((assigned.data ?? []).map((i) => i.id));
  const available = (instructors.data ?? []).filter((i) => !assignedIds.has(i.id));

  async function handleGenerateSessions() {
    try {
      await generateSessions.mutateAsync(id);
      toast({
        title: 'Sessions generated',
        description: 'One per weekday across the term.',
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: "Couldn't generate sessions",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="space-y-10">
      <div>
        <Link
          to="/admin/classes"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Classes
        </Link>
        <h1 className="mt-2">{c.name}</h1>
        <p className="mt-1 text-[var(--color-ink-muted)] capitalize">
          {c.style} · {c.level} · {c.weekday} {formatTime12(c.start_time)}–
          {formatTime12(c.end_time)} · {c.room}
        </p>
      </div>

      <section aria-labelledby="instructors-heading" className="surface-elevated p-6">
        <div className="flex items-center justify-between">
          <h2 id="instructors-heading" className="font-medium text-[var(--text-lg)]">
            Instructors
          </h2>
        </div>

        {assigned.data?.length === 0 && (
          <p className="mt-3 text-[var(--color-ink-muted)]">No instructors assigned yet.</p>
        )}

        {assigned.data && assigned.data.length > 0 && (
          <ul className="mt-4 grid gap-2">
            {assigned.data.map((i) => (
              <li key={i.id} className="surface flex items-center justify-between p-3">
                <span className="font-medium">
                  {i.first_name} {i.last_name}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => unassign.mutate({ classId: id, instructorId: i.id })}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}

        {available.length > 0 && (
          <details className="mt-4">
            <summary className="cursor-pointer text-[var(--color-accent)] text-[var(--text-sm)]">
              Assign instructor…
            </summary>
            <ul className="mt-3 grid gap-2">
              {available.map((i) => (
                <li key={i.id} className="surface flex items-center justify-between p-3">
                  <span>
                    {i.first_name} {i.last_name}
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => assign.mutate({ classId: id, instructorId: i.id })}
                  >
                    Assign
                  </Button>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      <section aria-labelledby="sessions-heading" className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 id="sessions-heading" className="text-[var(--text-xl)]">
            Sessions
          </h2>
          <Button
            variant="secondary"
            onClick={() => void handleGenerateSessions()}
            disabled={generateSessions.isPending}
          >
            {generateSessions.isPending ? 'Generating…' : 'Generate term sessions'}
          </Button>
        </div>

        {sessions.data?.length === 0 && (
          <p className="text-[var(--color-ink-muted)]">
            No sessions yet. Click <em>Generate term sessions</em> to create one row per weekday
            across the term.
          </p>
        )}

        {sessions.data && sessions.data.length > 0 && (
          <div role="region" aria-label="Sessions table" className="surface overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-[var(--color-surface-sunken)] text-[var(--text-sm)]">
                <tr>
                  <th scope="col" className="px-4 py-3 font-medium">
                    Date
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    Time
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    Status
                  </th>
                  <th scope="col" className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {sessions.data.map((s) => (
                  <tr key={s.id} className="border-t border-[var(--color-border)]">
                    <td className="px-4 py-3 tabular-nums">{s.session_date}</td>
                    <td className="px-4 py-3 text-[var(--color-ink-muted)] tabular-nums">
                      {formatTime12(s.start_time)}–{formatTime12(s.end_time)}
                    </td>
                    <td className="px-4 py-3">
                      {s.cancelled ? (
                        <Badge tone="warn">cancelled</Badge>
                      ) : (
                        <Badge tone="success">scheduled</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!s.cancelled && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            cancelSession.mutate({ id: s.id, reason: 'admin-cancelled' })
                          }
                        >
                          Cancel
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
